import Route from 'mewe/routes/route';
import { Theme } from 'mewe/constants';

import { fetchFeedMyWorld, getMyWorldFeed } from 'mewe/fetchers/fetch-feed';
import { fetchWrappers } from 'mewe/fetchers/fetch-wrappers';
import fetchProfile from 'mewe/fetchers/fetch-profile';
import { fetchPending } from 'mewe/fetchers/fetch-followers';
import { fetchTellers, fetchEntriesByTeller } from 'mewe/fetchers/fetch-journals';
import AccountApi from 'mewe/api/account-api';
import CurrentUserStore from 'mewe/stores/current-user-store';
import { showGroupPreviewDialog, startFtue } from 'mewe/utils/dialogs-common';

import ContactsStore from 'mewe/stores/contacts-store';
import ProfileStore from 'mewe/stores/profile-store';
import { WrapperScope } from 'mewe/constants';
import Session from 'mewe/shared/session';
import 'mewe/services/chat';
import { ds } from 'mewe/stores/ds';
import Storage from 'mewe/shared/storage';
import { FeedTypes } from 'mewe/constants';
import { inject as service } from '@ember/service';
import EmberObject from '@ember/object';
import { isPresent } from '@ember/utils';

export const MyWorldIndexRouteBase = Route.extend({
  theme: Theme.CONTACTS,

  journals: service(),

  account: service(),

  router: service(),

  settings: service(),

  dynamicDialogs: service(),

  ChatService: service('chat'),

  feedTypes: FeedTypes,

  deactivate() {
    this._super();
    if (this.controller?.get('tag')) {
      this.controller.set('tag', null);
      history.replaceState('', document.title, window.location.pathname); // prevent loading feed for tag on back navigation
    }
    // with group preview the FTUE is postponed until after group preview
    // will be closed OR - if user will join group - until next Home view opening
    if (this.controller?.get('previewGroupId')) {
      this.controller.set('previewGroupId', null);
    }
  },

  beforeModel(transition) {
    const { to: toRouteInfo } = transition;

    this.tag = toRouteInfo.queryParams.tag;
    this.scope = toRouteInfo.queryParams.scope;
    this.filter = toRouteInfo.queryParams.filter;
    this.previewGroupId = toRouteInfo.queryParams.previewGroupId;
    this.removedCredentials = toRouteInfo.queryParams.removedCredentials;

    // This logic will be processed based on the feature flag
    const scopeBasedOnFeatureFlags = ['groups', 'pages', 'thread'];
    if (scopeBasedOnFeatureFlags.includes(this.scope)) {
      const isCommentsEnabled = this.account.flagCommentsFeed;
      const isThreadScope = this.scope === 'thread';

      if ((isThreadScope && !isCommentsEnabled) || (!isThreadScope && isCommentsEnabled)) {
        transition.abort();
        this.router.transitionTo('app.myworld');
      }
    }

    if (this.scope === this.feedTypes.CONTACTS && Storage.get(Storage.keys.filterFavoritesInContacts)) {
      this.filter = this.feedTypes.FAVORITES;
    }

    if (this.scope === 'hashtag' && !this.tag) {
      this.scope = FeedTypes.ALL;
    }

    if (Session.hasAuthCookie()) {
      fetchFeedMyWorld(this.scope, { hashtag: this.tag }, this.filter);

      fetchWrappers(WrapperScope.ALL);

      fetchProfile();

      fetchPending();

      fetchTellers().then(() => {
        ds.tellers.items.forEach((el) => {
          this.journals.preload(el.get('stories')[0]);
        });
      });

      this.getUserVariants();
    }
  },

  model(params) {
    let model = EmberObject.extend({
      feed: getMyWorldFeed(this.scope, { hashtag: params.tag }),
      wrappers: ds.wrappers,
      wrappersScope: WrapperScope.ALL,
      app: this.modelFor('app'),
      chatFilter: this.ChatService.readChatFilter(),
      tellers: ds.tellers,
      featured: ds.featuredAll,
      followed: ds.followed,
      pages: ds.pages,
      tag: params.tag,
      feedFilter: this.filter,
      feedScope: this.scope,
    }).create();

    this.account.activeUser?.deferred?.promise?.then(() => {
      const userId = this.account.activeUser.id;

      fetchEntriesByTeller({ id: userId }).then(() => {
        this.journals.preload(ds.entriesByTeller.for(userId).items[0], true);
      });

      model.setProperties({
        profile: ProfileStore.getState({ uuid: userId }),
        requests: ContactsStore.getState({ id: userId + '-requests' }),
        myEntries: ds.entriesByTeller.for(userId),
      });

      if (this.removedCredentials) {
        // postponed until activeUser is ready to know his identifier
        const newIdentifier = this.account.activeUser.primaryEmail || this.account.activeUser.primaryPhoneNumber;

        this.dynamicDialogs.openDialog('simple-dialog-new', {
          title: __('Account updated'),
          okButtonText: __(`Close`),
          message: __(
            `From now on you can only use  {identifier} to log in to MeWe. All other emails and/or phone numbers have been removed from your account.`,
            { identifier: newIdentifier }
          ),
        });
      }

      this.showDialogs(params);
    });

    // first page load without scope param - fetch scope from settings
    if (!this.scope) {
      this.settings.promise().then(() => {
        // default will be ALL
        let scope = FeedTypes.ALL;
        // if there is any value saved on BE then use it, but only if it's not hashtag
        if (this.settings.homeFeedScope && this.settings.homeFeedScope !== 'hashtag') {
          scope = this.settings.homeFeedScope;
        }
        this.controller?.setFeedParams?.({ scope: scope, tag: this.tag });
      });
    }

    if (this.previewGroupId) {
      // important to send EmberObject as param
      showGroupPreviewDialog(this.dynamicDialogs, EmberObject.create({ id: this.previewGroupId }), {
        isFtueGroupPreview: true,
        context: isPresent(params.context) && params.context !== 'null' ? params.context : 'link_preview',
        onClose: () => {
          this.controller.set('previewGroupId', null);
        },
      });
    } else {
      startFtue(this.dynamicDialogs);
    }

    return model;
  },

  async getUserVariants() {
    if (!this.account.userVariant) {
      try {
        const campaignData = await AccountApi.getCampaign('i-support-mewe');

        CurrentUserStore.send('handle', {
          affiliation: campaignData?.affiliation,
        });
      } catch (e) {
        const campaignData = await AccountApi.getCampaign('i-support-mewe');

        CurrentUserStore.send('handle', {
          affiliation: campaignData?.affiliation,
        });
      }
    }
  },

  showDialogs(params) {
    const dialogConfigs = {
      createGroup: {
        name: 'group-create',
        options: {}
      },
      inviteFriends: {
        name: 'invitations-dialog',
        options: { isContactInvitation: true }
      },
      browseGroups: {
        name: 'browse-communities-dialog',
        options: { theme: Theme.GROUP }
      },
      createPage: {
        name: 'create-page-dialog',
        options: {}
      },
      createEvent: {
        name: 'event-create-dialog',
        options: {}
      }
    };

    const { show } = params;
    const config = dialogConfigs[show];

    if (config) {
      this.dynamicDialogs.openDialog(config.name, {
        ...config.options,
        onClose: () => {
          this.controller.set('show', null);
        }
      });
    }
  },

  actions: {
    didTransition() {
      this.cleanRouteHash();
      this.dynamicDialogs.closeAll();
      window.scrollTo(0, 0);
      return true;
    },
  },
});

// TODO: refreshModel should be true for queryParams to tigger the model refresh and data down approach
export default MyWorldIndexRouteBase.extend({
  queryParams: {
    tag: {
      refreshModel: true,
    },
    scope: {
      refreshModel: false,
    },
    filter: {
      refreshModel: false,
    },
    previewGroupId: {
      refreshModel: false,
    },
  },
});
